import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";

/**
 * SuperuseryAdmin — správa seznamu superuserů (PRM Vodafone loginů)
 * Admin vloží seznam loginů (z Excelu), systém pak zvýrazní políčko PRM Vodafone
 * na kartě zákazníka, pokud se hodnota shoduje s některým loginem zde.
 */
export default function SuperuseryAdmin({ isDarkMode, onClose }) {
  const [rawText, setRawText] = useState("");
  const [savedLogins, setSavedLogins] = useState([]);
  const [freeRawText, setFreeRawText] = useState("");
  const [savedFreeLogins, setSavedFreeLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [freeSaving, setFreeSaving] = useState(false);
  const [freeSaveMsg, setFreeSaveMsg] = useState(null);

  const bg = isDarkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const inputBg = isDarkMode
    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400";

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('lastUserId') || '';
      const res = await fetch(`${API_URL}/superusery?userId=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setSavedLogins(data.logins || []);
        setRawText((data.logins || []).join("\n"));
        setSavedFreeLogins(data.freeLogins || []);
        setFreeRawText((data.freeLogins || []).join("\n"));
      }
    } catch (e) {
      console.error("Chyba při načítání superuserů:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Parsuje textarea — jeden login na řádek nebo tabulátory/čárky (jak Excel kopíruje)
  const parseLogins = (text) => {
    return [...new Set(
      text
        .split(/[\n\r\t,;]+/)
        .map(l => l.trim())
        .filter(Boolean)
    )];
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveMsg(null);
      const userId = localStorage.getItem('lastUserId') || '';
      const logins = parseLogins(rawText);
      const res = await fetch(`${API_URL}/superusery`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ logins, userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedLogins(logins);
        setRawText(logins.join("\n"));
        setSaveMsg({ type: "ok", text: `Uloženo ${data.count} loginů.` });
        // Invalidate cache pro zvýraznění PRM v CompanyForm
        window.__superuseryLogins = logins;
        window.__superuseryLoadedAt = Date.now();
      } else {
        setSaveMsg({ type: "err", text: "Chyba při ukládání." });
      }
    } catch (e) {
      setSaveMsg({ type: "err", text: "Chyba při ukládání: " + e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  const handleSaveFree = async () => {
    try {
      setFreeSaving(true);
      setFreeSaveMsg(null);
      const userId = localStorage.getItem('lastUserId') || '';
      const freeLogins = parseLogins(freeRawText);
      const res = await fetch(`${API_URL}/superusery`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ freeLogins, userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedFreeLogins(freeLogins);
        setFreeRawText(freeLogins.join("\n"));
        setFreeSaveMsg({ type: "ok", text: `Uloženo ${data.freeCount} loginů.` });
      } else {
        setFreeSaveMsg({ type: "err", text: "Chyba při ukládání." });
      }
    } catch (e) {
      setFreeSaveMsg({ type: "err", text: "Chyba při ukládání: " + e.message });
    } finally {
      setFreeSaving(false);
      setTimeout(() => setFreeSaveMsg(null), 3000);
    }
  };

  const preview = parseLogins(rawText);
  const freePreview = parseLogins(freeRawText);

  return (
    <div className={`min-h-screen p-6 ${bg}`}>
      {/* Hlavička */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">🔑 VF Loginy</h1>
          <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Správa VF loginů – superuserů (zvýraznění PRM) a loginů volných k obchodování (kontrola přeblokace).
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            ✕ Zavřít
          </button>
        )}
      </div>

      {/* ── Sekce 1: Superusery ───────────────────────────────────────────── */}
      <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
        <span>⭐</span> Superusery (PRM loginy)
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mb-10">
        {/* Vstupní pole */}
        <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
          <h3 className="font-semibold text-base mb-2">📋 Vložit / upravit seznam</h3>
          <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Zkopírujte sloupec z Excelu a vložte sem. Každý login na nový řádek,
            nebo oddělený tabulátorem / čárkou / středníkem.
          </p>
          <textarea
            className={`w-full h-64 px-3 py-2 rounded-lg border text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-yellow-400 ${inputBg}`}
            placeholder={"login1\nlogin2\nlogin3\n..."}
            value={rawText}
            onChange={e => setRawText(e.target.value)}
          />
          <div className="flex items-center justify-between mt-3 gap-3">
            <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {preview.length > 0 ? `${preview.length} loginů k uložení` : "Žádné loginy"}
            </span>
            <div className="flex items-center gap-2">
              {saveMsg && (
                <span className={`text-xs font-medium ${saveMsg.type === "ok" ? "text-green-500" : "text-red-500"}`}>
                  {saveMsg.text}
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                  saving
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-yellow-500 hover:bg-yellow-600 text-white"
                }`}
              >
                {saving ? "Ukládám…" : "💾 Uložit"}
              </button>
            </div>
          </div>
        </div>

        {/* Aktuálně uložené */}
        <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
          <h3 className="font-semibold text-base mb-2">
            ✅ Uložené loginy{" "}
            <span className={`text-xs font-normal ml-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              ({loading ? "…" : savedLogins.length})
            </span>
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-yellow-400 border-t-transparent" />
            </div>
          ) : savedLogins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-2">📭</div>
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Žádné loginy zatím uloženy.
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64 rounded-lg border divide-y divide-gray-200 dark:divide-gray-700">
              {savedLogins.map((login, i) => (
                <div
                  key={i}
                  className={`px-3 py-2 flex items-center justify-between text-sm font-mono ${
                    i % 2 === 0
                      ? isDarkMode ? "bg-gray-800" : "bg-white"
                      : isDarkMode ? "bg-gray-750" : "bg-gray-50"
                  }`}
                >
                  <span>
                    <span className={`mr-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{i + 1}.</span>
                    {login}
                  </span>
                  <span className="text-yellow-500">⭐</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Sekce 2: VF Loginy volné k obchodování ────────────────────────── */}
      <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
        <span>🟢</span> VF Loginy volné k obchodování
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Vstupní pole */}
        <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
          <h3 className="font-semibold text-base mb-2">📋 Vložit / upravit seznam</h3>
          <p className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            VF loginy, které jsou volné k obchodování. Pokud se Vlastník (původní vlastník)
            ze Siebelu shoduje s některým z těchto loginů, přeblokace je povolena.
            Každý login na nový řádek, nebo oddělený tabulátorem / čárkou / středníkem.
          </p>
          <textarea
            className={`w-full h-64 px-3 py-2 rounded-lg border text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-400 ${inputBg}`}
            placeholder={"VFLOGIN1\nVFLOGIN2\nVFLOGIN3\n..."}
            value={freeRawText}
            onChange={e => setFreeRawText(e.target.value)}
          />
          <div className="flex items-center justify-between mt-3 gap-3">
            <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {freePreview.length > 0 ? `${freePreview.length} loginů k uložení` : "Žádné loginy"}
            </span>
            <div className="flex items-center gap-2">
              {freeSaveMsg && (
                <span className={`text-xs font-medium ${freeSaveMsg.type === "ok" ? "text-green-500" : "text-red-500"}`}>
                  {freeSaveMsg.text}
                </span>
              )}
              <button
                onClick={handleSaveFree}
                disabled={freeSaving}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                  freeSaving
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                {freeSaving ? "Ukládám…" : "💾 Uložit"}
              </button>
            </div>
          </div>
        </div>

        {/* Aktuálně uložené */}
        <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
          <h3 className="font-semibold text-base mb-2">
            ✅ Uložené loginy{" "}
            <span className={`text-xs font-normal ml-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              ({loading ? "…" : savedFreeLogins.length})
            </span>
          </h3>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-400 border-t-transparent" />
            </div>
          ) : savedFreeLogins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-2">📭</div>
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Žádné loginy zatím uloženy.
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64 rounded-lg border divide-y divide-gray-200 dark:divide-gray-700">
              {savedFreeLogins.map((login, i) => (
                <div
                  key={i}
                  className={`px-3 py-2 flex items-center justify-between text-sm font-mono ${
                    i % 2 === 0
                      ? isDarkMode ? "bg-gray-800" : "bg-white"
                      : isDarkMode ? "bg-gray-750" : "bg-gray-50"
                  }`}
                >
                  <span>
                    <span className={`mr-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{i + 1}.</span>
                    {login}
                  </span>
                  <span className="text-green-500">🟢</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info box */}
      <div className={`mt-6 max-w-5xl rounded-xl border p-4 text-sm ${isDarkMode ? "bg-gray-800 border-green-700 text-gray-300" : "bg-green-50 border-green-200 text-gray-700"}`}>
        <strong className="text-green-600 dark:text-green-400">ℹ️ Jak to funguje:</strong>{" "}
        Při automatické přeblokaci systém zkontroluje Vlastníka (původního vlastníka) ze Siebelu.
        Pokud se shoduje s některým <em>VF loginem volným k obchodování</em> nebo s VF loginem
        některého obchodníka, přeblokace pokračuje automaticky. Jinak nabídne odeslání žádosti
        o uvolnění nebo vyplnění formuláře bez odeslání.
      </div>
    </div>
  );
}
